import { addDays, addHours, parseISO, set, subDays, subHours } from 'date-fns'

type WorkPeriodDayExtensionTimeEntry = {
  datetimeStart: string
  datetimeEnd: string | null
}

export type WorkPeriodDayExtensionState = {
  currentStart: Date
  currentEnd: Date
  beforeExtensionStart: Date | null
  afterExtensionEnd: Date | null
}

export type WorkPeriodExtensionSide = 'before' | 'after'

export type WorkPeriodDayExtensionEntry = {
  side: WorkPeriodExtensionSide
  start: Date
  end: Date
}

export const WORK_PERIOD_DAY_EXTENSION_LIMIT_MS = 24 * 60 * 60 * 1000

export const getWorkPeriodDayExtensionState = ({
  workPeriodStart,
  workPeriodEnd,
  timeEntries,
}: {
  workPeriodStart: Date
  workPeriodEnd: Date
  timeEntries: WorkPeriodDayExtensionTimeEntry[]
}): WorkPeriodDayExtensionState => {
  let currentStart = workPeriodStart
  let currentEnd = workPeriodEnd
  let beforeExtensionStart: Date | null = null
  let afterExtensionEnd: Date | null = null

  timeEntries.forEach(entry => {
    const entryStart = parseISO(entry.datetimeStart)
    const entryEnd = entry.datetimeEnd
      ? parseISO(entry.datetimeEnd)
      : parseISO(entry.datetimeStart)

    if (entryStart < currentStart) {
      currentStart = entryStart
    }

    if (entryEnd > currentEnd) {
      currentEnd = entryEnd
    }

    if (entryStart < workPeriodStart) {
      if (!beforeExtensionStart || entryStart < beforeExtensionStart) {
        beforeExtensionStart = entryStart
      }
    }

    if (entryEnd > workPeriodEnd) {
      if (!afterExtensionEnd || entryEnd > afterExtensionEnd) {
        afterExtensionEnd = entryEnd
      }
    }
  })

  return {
    currentStart,
    currentEnd,
    beforeExtensionStart,
    afterExtensionEnd,
  }
}

export const getInitialWorkPeriodDayExtensionRange = ({
  side,
  currentStart,
  currentEnd,
}: {
  side: WorkPeriodExtensionSide
  currentStart: Date
  currentEnd: Date
}) => {
  if (side === 'before') {
    return {
      start: subHours(currentStart, 1),
      end: currentStart,
    }
  }

  return {
    start: currentEnd,
    end: addHours(currentEnd, 1),
  }
}

export const isSameTimeOfDay = (left: Date, right: Date) =>
  left.getHours() === right.getHours() &&
  left.getMinutes() === right.getMinutes()

export const resolveWorkPeriodExtensionBoundary = ({
  side,
  boundary,
  timeOfDay,
}: {
  side: WorkPeriodExtensionSide
  boundary: Date
  timeOfDay: Date
}) => {
  const candidate = set(boundary, {
    hours: timeOfDay.getHours(),
    minutes: timeOfDay.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  })

  if (side === 'before') {
    return candidate < boundary ? candidate : subDays(candidate, 1)
  }

  return candidate > boundary ? candidate : addDays(candidate, 1)
}

export const getWorkPeriodDayExtensionRange = ({
  side,
  currentStart,
  currentEnd,
  timeOfDay,
}: {
  side: WorkPeriodExtensionSide
  currentStart: Date
  currentEnd: Date
  timeOfDay: Date
}) => {
  if (side === 'before') {
    return {
      start: resolveWorkPeriodExtensionBoundary({
        side,
        boundary: currentStart,
        timeOfDay,
      }),
      end: currentStart,
    }
  }

  return {
    start: currentEnd,
    end: resolveWorkPeriodExtensionBoundary({
      side,
      boundary: currentEnd,
      timeOfDay,
    }),
  }
}

export const getWorkPeriodDayExtensionPreviewRange = ({
  currentStart,
  currentEnd,
  startTimeOfDay,
  endTimeOfDay,
}: {
  currentStart: Date
  currentEnd: Date
  startTimeOfDay: Date
  endTimeOfDay: Date
}) => {
  const previewStart = isSameTimeOfDay(startTimeOfDay, currentStart)
    ? currentStart
    : resolveWorkPeriodExtensionBoundary({
        side: 'before',
        boundary: currentStart,
        timeOfDay: startTimeOfDay,
      })

  const previewEnd = isSameTimeOfDay(endTimeOfDay, currentEnd)
    ? currentEnd
    : resolveWorkPeriodExtensionBoundary({
        side: 'after',
        boundary: currentEnd,
        timeOfDay: endTimeOfDay,
      })

  return {
    start: previewStart,
    end: previewEnd,
  }
}

export const getWorkPeriodDayExtensionEntries = ({
  currentStart,
  currentEnd,
  startTimeOfDay,
  endTimeOfDay,
}: {
  currentStart: Date
  currentEnd: Date
  startTimeOfDay: Date
  endTimeOfDay: Date
}): WorkPeriodDayExtensionEntry[] => {
  const entries: WorkPeriodDayExtensionEntry[] = []

  if (!isSameTimeOfDay(startTimeOfDay, currentStart)) {
    entries.push({
      side: 'before',
      start: resolveWorkPeriodExtensionBoundary({
        side: 'before',
        boundary: currentStart,
        timeOfDay: startTimeOfDay,
      }),
      end: currentStart,
    })
  }

  if (!isSameTimeOfDay(endTimeOfDay, currentEnd)) {
    entries.push({
      side: 'after',
      start: currentEnd,
      end: resolveWorkPeriodExtensionBoundary({
        side: 'after',
        boundary: currentEnd,
        timeOfDay: endTimeOfDay,
      }),
    })
  }

  return entries
}

export const isWorkPeriodDayExtensionWithinLimit = ({
  start,
  end,
}: {
  start: Date
  end: Date
}) => end.getTime() - start.getTime() <= WORK_PERIOD_DAY_EXTENSION_LIMIT_MS
